import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

// نموذج للصور في السلايدر (يستخدم فقط إذا لم تكن هناك بانرات في الإعدادات)
const fallbackSliderItems = [
  {
    id: '1',
    imageUrl: 'https://via.placeholder.com/1920x500/3f51b5/ffffff?text=عروض+خاصة',
    title: 'عروض خاصة',
    subtitle: 'خصومات تصل إلى 50% على جميع المنتجات الإلكترونية',
    buttonText: 'تسوق الآن',
    buttonLink: '/store/categories',
    backgroundColor: '#3f51b5',
    textColor: '#ffffff'
  },
  {
    id: '2',
    imageUrl: 'https://via.placeholder.com/1920x500/f50057/ffffff?text=وصل+حديثاً',
    title: 'وصل حديثاً',
    subtitle: 'أحدث المنتجات من أشهر الماركات العالمية',
    buttonText: 'اكتشف المزيد',
    buttonLink: '/store/categories',
    backgroundColor: '#f50057',
    textColor: '#ffffff'
  },
  {
    id: '3',
    imageUrl: 'https://via.placeholder.com/1920x500/4caf50/ffffff?text=توصيل+مجاني',
    title: 'توصيل مجاني',
    subtitle: 'للطلبات التي تزيد قيمتها عن 200 ريال',
    buttonText: 'اطلب الآن',
    buttonLink: '/store/categories',
    backgroundColor: '#4caf50',
    textColor: '#ffffff'
  }
];

const HomeSlider: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { settings, loading } = useStoreSettings();

  // استخدام البانرات من إعدادات المتجر أو استخدام البيانات الاحتياطية إذا لم تكن متوفرة
  const banners = settings?.display?.banners && settings.display.banners.length > 0
    ? settings.display.banners
    : fallbackSliderItems;

  const maxSteps = banners.length;

  // التنقل للسلايد التالي
  const handleNext = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
  };

  // التنقل للسلايد السابق
  const handleBack = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps);
  };

  // التنقل لسلايد محدد
  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  // إيقاف التشغيل التلقائي عند تمرير المؤشر
  const handleMouseEnter = () => {
    setAutoPlay(false);
  };

  // استئناف التشغيل التلقائي عند إزالة المؤشر
  const handleMouseLeave = () => {
    setAutoPlay(true);
  };

  // التشغيل التلقائي للسلايدر
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (autoPlay) {
      timer = setInterval(() => {
        handleNext();
      }, 5000); // تغيير السلايد كل 5 ثوانٍ
    }

    return () => {
      clearInterval(timer);
    };
  }, [autoPlay, activeStep]);

  // إذا كانت البيانات قيد التحميل، عرض البانر الأول من البيانات الاحتياطية
  const currentSlide = banners[activeStep];

  // التحقق من إعدادات ظهور البانر
  if (settings?.display?.showBanners === false) {
    return null;
  }

  // عرض مؤشر التحميل إذا كانت البيانات قيد التحميل ولم تكن هناك بانرات
  if (loading && (!settings?.display?.banners || settings.display.banners.length === 0)) {
    return (
      <Box
        sx={{
          width: '100%',
          height: { xs: '250px', sm: '400px', md: '500px' },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 4
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <Box
      className="main-slider"
      sx={{
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
        mb: 4,
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* صورة السلايدر */}
      <Box
        sx={{
          height: { xs: '250px', sm: '400px', md: '500px' },
          width: '100%',
          backgroundImage: `url(${currentSlide.imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          transition: 'all 0.5s ease-in-out',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* محتوى السلايدر */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: { xs: '5%', md: '10%' },
            transform: 'translateY(-50%)',
            textAlign: 'right',
            color: currentSlide.textColor || 'white',
            zIndex: 1,
            maxWidth: { xs: '80%', md: '50%' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
          }}
        >
          <Typography
            variant={isMobile ? 'h5' : 'h3'}
            component="h2"
            sx={{
              fontWeight: 'bold',
              mb: 2
            }}
          >
            {currentSlide.title}
          </Typography>

          <Typography
            variant={isMobile ? 'body2' : 'h6'}
            sx={{
              mb: 3,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {currentSlide.subtitle}
          </Typography>

          <Button
            variant="contained"
            color="secondary"
            size={isMobile ? 'small' : 'large'}
            component={RouterLink}
            to={currentSlide.buttonLink || '/store/categories'}
            sx={{
              fontWeight: 'bold',
              px: { xs: 2, md: 4 },
              py: { xs: 1, md: 1.5 },
              borderRadius: '4px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            }}
          >
            {currentSlide.buttonText}
          </Button>
        </Box>
      </Box>

      {/* أزرار التنقل */}
      <Button
        sx={{
          position: 'absolute',
          top: '50%',
          right: { xs: 8, md: 16 },
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          color: '#000',
          minWidth: { xs: '30px', md: '40px' },
          width: { xs: '30px', md: '40px' },
          height: { xs: '30px', md: '40px' },
          borderRadius: '50%',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          },
          zIndex: 2,
        }}
        onClick={handleNext}
      >
        <ArrowBack />
      </Button>

      <Button
        sx={{
          position: 'absolute',
          top: '50%',
          left: { xs: 8, md: 16 },
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          color: '#000',
          minWidth: { xs: '30px', md: '40px' },
          width: { xs: '30px', md: '40px' },
          height: { xs: '30px', md: '40px' },
          borderRadius: '50%',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          },
          zIndex: 2,
        }}
        onClick={handleBack}
      >
        <ArrowForward />
      </Button>

      {/* مؤشرات السلايدر */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
          zIndex: 2,
        }}
      >
        {banners.map((_, index: number) => (
          <Box
            key={index}
            onClick={() => handleStepChange(index)}
            sx={{
              width: { xs: 8, md: 12 },
              height: { xs: 8, md: 12 },
              borderRadius: '50%',
              backgroundColor: index === activeStep ? '#fff' : 'rgba(255, 255, 255, 0.5)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: index === activeStep ? '#fff' : 'rgba(255, 255, 255, 0.8)',
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default HomeSlider;
